/**
 * Interaction events that count as "the user has started using the page".
 * Marketing tags that gate on first interaction (Ads, FB Pixel) only need to
 * fire once any of these happens, so we listen to all of them and tear the
 * listeners down on the first hit.
 */
const INTERACTION_EVENTS = [
  'pointerdown',
  'keydown',
  'touchstart',
  'scroll',
] as const;

/** Hard ceiling so users who bounce without interacting still get tagged. */
const FALLBACK_TIMEOUT_MS = 3500;

/**
 * Runs `callback` exactly once, off the critical render path, on the earliest
 * of: the first user interaction, the first idle period after load, or a
 * safety timeout. This keeps heavy third-party tag scripts (GTM and the
 * marketing pixels it cascades to) from competing with the Vue app and its
 * LCP element during initial paint, while still firing reliably so analytics
 * (e.g. GA4 page_view) is captured even for users who bounce immediately.
 */
export const runWhenIdleOrInteraction = (callback: () => void): void => {
  let fired = false;

  const cleanup = () => {
    for (const eventName of INTERACTION_EVENTS) {
      window.removeEventListener(eventName, run);
    }
  };

  const run = () => {
    if (fired) {
      return;
    }
    fired = true;
    cleanup();
    callback();
  };

  for (const eventName of INTERACTION_EVENTS) {
    window.addEventListener(eventName, run, { once: true, passive: true });
  }

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: FALLBACK_TIMEOUT_MS });
  } else {
    window.setTimeout(run, FALLBACK_TIMEOUT_MS);
  }
};
